<template>
  <div class="input-action-menu">
    <ChatImageUploadButton
      :upload-url="uploadAction"
      :headers="uploadHeader"
      @upload:success="handleImageSuccess"
    >
      <template #default="{ openFilePicker }">
        <button
          type="button"
          class="menu-item"
          :aria-label="$t('session.chat.image')"
          @click="handleOpenPicker(openFilePicker)"
        >
          <span class="menu-item-icon"><ImageLargeIcon /></span>
          <span>{{ $t('session.chat.image') }}</span>
        </button>
      </template>
    </ChatImageUploadButton>

    <ChatFileUploadButton
      :upload-url="uploadAction"
      :headers="uploadHeader"
      @upload:success="handleFileSuccess"
    >
      <template #default="{ openFilePicker }">
        <button
          type="button"
          class="menu-item"
          :aria-label="$t('session.chat.file')"
          @click="handleOpenPicker(openFilePicker)"
        >
          <span class="menu-item-icon"><AttachLargeIcon /></span>
          <span>{{ $t('session.chat.file') }}</span>
        </button>
      </template>
    </ChatFileUploadButton>

    <button
      v-if="isCommentEnabled"
      type="button"
      class="menu-item"
      :aria-label="$t('session.chat.comment')"
      @click="handleComment"
    >
      <span class="menu-item-icon"><SubscribeLargeIcon /></span>
      <span>{{ $t('session.chat.comment') }}</span>
    </button>
  </div>
</template>

<script lang="ts">
/**
 * Purpose: Render the unified image, file, and optional rating actions above the Widget composer.
 * State: Rating availability is loaded once from the existing channel configuration endpoint.
 * Emits: image-success and file-success forward normalized upload results; close requests that the
 * owning composer dismiss the menu before an external picker or rating request runs.
 */
import { defineComponent, onMounted, ref } from 'vue'
import { getGradeConfig, sendChatGrade } from '../../../api/simple-api'
import { useUpload } from '../../../hooks/useUpload'
import type { ChatFileUploadResult, ChatImageUploadData } from '../../../hooks/useChatInput'
import { useChatStore } from '../../../store/chat'
import { getChannelId } from '../../../utils/channelStorage'
import ChatFileUploadButton from '../../button/ChatFileUploadButton.vue'
import ChatImageUploadButton from '../../button/ChatImageUploadButton.vue'
import AttachLargeIcon from '../../icon/AttachLargeIcon.vue'
import ImageLargeIcon from '../../icon/ImageLargeIcon.vue'
import SubscribeLargeIcon from '../../icon/SubscribeLargeIcon.vue'

interface ImageUploadResponse {
  data: ChatImageUploadData
}

interface GradeConfigResponse {
  code: string
  data: boolean
}

export default defineComponent({
  name: 'InputActionMenu',
  components: {
    AttachLargeIcon,
    ChatFileUploadButton,
    ChatImageUploadButton,
    ImageLargeIcon,
    SubscribeLargeIcon,
  },
  emits: {
    close: () => true,
    'image-success': (data: ChatImageUploadData) => Boolean(data),
    'file-success': (data: ChatFileUploadResult) => Boolean(data),
  },
  setup(_, { emit }) {
    const cStore = useChatStore()
    const isCommentEnabled = ref(false)
    const { uploadHeader, uploadAction } = useUpload('/chats/chat/uploadFile')

    const handleOpenPicker = (openFilePicker: () => void) => {
      emit('close')
      openFilePicker()
    }

    const handleImageSuccess = (response: ImageUploadResponse) => {
      emit('image-success', response.data)
    }

    const handleFileSuccess = (response: ChatFileUploadResult) => {
      emit('file-success', response)
    }

    const handleComment = () => {
      emit('close')
      void sendChatGrade(getChannelId(), { chatId: cStore.chatId })
    }

    onMounted(async () => {
      const response = await getGradeConfig(getChannelId())
      const config = (await response.json()) as GradeConfigResponse
      isCommentEnabled.value = config.code === '00000' && config.data
    })

    return {
      handleComment,
      handleFileSuccess,
      handleImageSuccess,
      handleOpenPicker,
      isCommentEnabled,
      uploadAction,
      uploadHeader,
    }
  },
})
</script>

<style lang="less" scoped>
.input-action-menu {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: 5;
  box-sizing: border-box;
  display: flex;
  width: 120px;
  padding: 4px;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.1);
}

.menu-item {
  display: flex;
  width: 100%;
  height: 40px;
  padding: 8px;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: var(--neutral-fill-2);
    outline: none;
  }
}

.menu-item-icon {
  display: grid;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  place-items: center;

  :deep(svg) {
    width: 16px;
    height: 16px;
  }
}
</style>
